import React, { useState } from 'react';
import './CreateJobAdvanced.scss';
import { createJobConfigHooks } from './hooks';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
} from '@mui/material';
import { JobDetailsCard } from './components/JobDetailsCard';
import { NotificationsCard } from './components/NotificationsCard';
import { ScheduleCard } from './components/ScheduleCard';
import { MappingTableCard } from './components/MappingTableCard';
import { ListTableCard } from './components/ListTableCard';

// Mirrors charlotte-jobs/src/mep-exportDemographics/schema.ts `defaultFilenameSchema`.
const DEFAULT_FILE_NAMING_SCHEMA =
  '/migrated/wurk/WURKDEMO_{PLAN_ID}_{YEAR_4_DIGITS}{MONTH_LEADING_ZERO}{DAY_LEADING_ZERO}_{HOUR_24}{MINUTE}{SECOND}.csv';

const DEFAULT_CONFIG = {
  kronos: {
    type: '1password_item',
    '1password_item_path': 'Client credentials/UKG - EnjoyWurkAdmin - apiuser',
  },
  sftp: {
    type: '1password_item',
    '1password_item_path': 'Client credentials/SFTP PCS',
  },
  emailConfig: {
    emailContacts: [],
    sendSuccessEmails: 'true',
  },
  reportDetails: {
    demographicReport: '',
    planID: '',
  },
  mapping: {},
  inactiveEINlist: [],
  // fileNamingSchema is intentionally absent by default: the job runner falls
  // back to defaultFilenameSchema (mirrored here as DEFAULT_FILE_NAMING_SCHEMA)
  // when it's unset, so we only write it once the override is actually enabled.
};

const ClientPlanCard = ({ useJobConfig }) => {
  const [companyName, setCompanyName] = useJobConfig(['companyName']);
  const [demographicReport, setDemographicReport] = useJobConfig(['reportDetails', 'demographicReport']);
  const [planID, setPlanID] = useJobConfig(['reportDetails', 'planID']);
  return (
    <Card>
      <CardHeader title="Client & Plan" subheader="Identifies which client and 401(k) plan this feed is for" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Company short name"
              variant="standard"
              helperText="Internal client identifier"
              required
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Demographics report ID"
              variant="standard"
              helperText="Saved Kronos report ID with employee demographics"
              required
              value={demographicReport}
              onChange={(e) => setDemographicReport(e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Plan ID"
              variant="standard"
              helperText="e.g. ITC001"
              required
              value={planID}
              onChange={(e) => setPlanID(e.target.value)}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const AdvancedCard = ({ useJobConfig }) => {
  const [fileNamingSchema, setFileNamingSchema] = useJobConfig(['fileNamingSchema']);
  const [custom, setCustom] = useState(fileNamingSchema !== undefined);

  return (
    <Card>
      <CardHeader title="Advanced / Experimental" subheader="Rarely needed" />
      <CardContent>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox />}
            checked={custom}
            onChange={(e, checked) => {
              setCustom(checked);
              setFileNamingSchema(checked ? DEFAULT_FILE_NAMING_SCHEMA : undefined);
            }}
            label={
              <>
                Override file naming schema
                <FormHelperText>
                  Defaults to <code>{DEFAULT_FILE_NAMING_SCHEMA}</code>. Only change this if the vendor requests a different pattern.
                </FormHelperText>
              </>
            }
          />
          <TextField
            fullWidth
            label="File naming schema"
            variant="standard"
            value={fileNamingSchema || DEFAULT_FILE_NAMING_SCHEMA}
            onChange={(e) => setFileNamingSchema(e.target.value)}
            disabled={!custom}
          />
        </FormGroup>
      </CardContent>
    </Card>
  );
};

const DemographicsFormBody = ({ partner, defaultConfig }) => {
  const { useJobGeneralConfig, useJobConfig } = createJobConfigHooks({ partner, defaultConfig });
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <JobDetailsCard useJobGeneralConfig={useJobGeneralConfig} />
      </Grid>
      <Grid item xs={12}>
        <ClientPlanCard useJobConfig={useJobConfig} />
      </Grid>
      <Grid item xs={12}>
        <MappingTableCard
          title="EIN → Division mapping"
          subheader="Maps each Employee EIN to its Division code used in the export."
          keyColumnLabel="Employee EIN"
          valueColumnLabel="Division code"
          keyPlaceholder="e.g. EIN5555"
          valuePlaceholder="e.g. P1"
          useJobConfig={useJobConfig}
          fieldPath={['mapping']}
        />
      </Grid>
      <Grid item xs={12}>
        <ListTableCard
          title="Inactive EINs"
          subheader="Employees to omit from the demographics export"
          columnLabel="Employee EIN"
          placeholder="e.g. EIN5555"
          emptyLabel="No inactive EINs configured"
          useJobConfig={useJobConfig}
          fieldPath={['inactiveEINlist']}
        />
      </Grid>
      <Grid item xs={12}>
        <NotificationsCard useJobConfig={useJobConfig} />
      </Grid>
      <Grid item xs={12}>
        <ScheduleCard useJobGeneralConfig={useJobGeneralConfig} />
      </Grid>
      <Grid item xs={12}>
        <AdvancedCard useJobConfig={useJobConfig} />
      </Grid>
    </Grid>
  );
};

export const MepExportDemographicsForm = () => <DemographicsFormBody partner="PCS" defaultConfig={DEFAULT_CONFIG} />;
export const PcsExportDemographicsForm = () => <DemographicsFormBody partner="PCS" defaultConfig={DEFAULT_CONFIG} />;
